import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getQueryToolStatus,
  getQueryToolSubscriberAvailability,
} from '../../models/api/query-tool';
import { queryToolBaseUrl } from './core/config';
import { getOriginalUser, getUserState } from '../../features/user/selectors';

/**
 * Build-time switch, off unless the build sets REACT_APP_ENABLE_CENTERLINE_AI=true. Without it a
 * deployed frontend neither shows CENTERLINE AI nor calls the Query Tool endpoints. With it, the
 * backend still decides who may use it (QUERY_TOOL_SITE_ADMINS_ONLY: site admins only, for now).
 */
export const CENTERLINE_AI_ENABLED = process.env.REACT_APP_ENABLE_CENTERLINE_AI === 'true';

/**
 * The Query Tool's origin (REACT_APP_QUERY_TOOL_URL), which the chat calls directly under `/v1`
 * with a token from CENTERLINE's backend. Null when the build sets none (dev and staging have no
 * Query Tool); the page then says CENTERLINE AI isn't configured.
 */
export const CENTERLINE_AI_CHAT_URL = queryToolBaseUrl(process.env.REACT_APP_QUERY_TOOL_URL);

/**
 * CENTERLINE AI's page (CenterlineAiPage.tsx). Not under /main/projects/:projectId, so switching
 * projects on it never goes through PrivateRoute's project loader; the project it shows is in the
 * query string instead (`?project=`, see centerlineAiPath).
 */
export const CENTERLINE_AI_PATH = '/main/centerline-ai';

/** A link to CENTERLINE AI's page, on `projectId` when given: `/main/centerline-ai?project=<id>`. */
export const centerlineAiPath = (projectId?: string | null) =>
  projectId ? `${CENTERLINE_AI_PATH}?project=${encodeURIComponent(projectId)}` : CENTERLINE_AI_PATH;

/** The project a CENTERLINE AI link names (`?project=`), or undefined when it names none. */
export const centerlineAiProjectFrom = (search: string) =>
  new URLSearchParams(search).get('project') || undefined;

/**
 * Whether this user sees anything of CENTERLINE AI in the frontend: while the production test runs,
 * site admins only (the real person, when a site admin impersonates someone), and only in builds
 * that enable it. Everyone else gets no drawer entry, page, setting or request. The backend enforces
 * the same rule on its own (QUERY_TOOL_SITE_ADMINS_ONLY); this keeps the frontend from showing or
 * asking anything for everyone else.
 */
export function useCenterlineAiVisible(): boolean {
  const siteAdmin = useIsSiteAdmin();
  return CENTERLINE_AI_ENABLED && siteAdmin;
}

/**
 * Whether the signed-in user is a site admin, or the real person behind an impersonation is. What
 * the CENTERLINE AI page keeps to site admins (the page itself, the composer's tools button) asks
 * this, so they all mean the same person.
 */
export function useIsSiteAdmin(): boolean {
  const user = useSelector(getUserState);
  const originalUser = useSelector(getOriginalUser);
  return !!user?.isSiteAdmin || !!originalUser?.isSiteAdmin;
}

/**
 * Whether the user can open CENTERLINE AI from `projectId`, as the backend's status route says (a
 * live, entitled project; during the pilot, full access to it; while the production test runs, a
 * site admin). Asks only for users useCenterlineAiVisible allows; false while it's asking.
 */
export function useCenterlineAiAvailable(projectId: string | undefined): boolean {
  const visible = useCenterlineAiVisible();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let current = true;
    setAvailable(false);
    if (visible && projectId) {
      getQueryToolStatus(projectId)
        .then((res) => current && setAvailable(res.available))
        .catch(() => current && setAvailable(false));
    }
    return () => {
      current = false;
    };
  }, [projectId, visible]);

  return available;
}

/**
 * Whether the security groups page shows the CENTERLINE AI setting for `subscriberId`, as the
 * backend's availability route says (the subscriber is entitled, and during the production test the
 * caller is a site admin). Asks only for users useCenterlineAiVisible allows; false while it's
 * asking.
 */
export function useCenterlineAiSubscriberAvailable(
  subscriberId: string | undefined | null,
): boolean {
  const visible = useCenterlineAiVisible();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let current = true;
    setAvailable(false);
    if (visible && subscriberId) {
      getQueryToolSubscriberAvailability(subscriberId)
        .then((res) => current && setAvailable(res))
        .catch(() => current && setAvailable(false));
    }
    return () => {
      current = false;
    };
  }, [subscriberId, visible]);

  return available;
}
